import { defineAsyncComponent, markRaw } from 'vue';
import { VueFinalModal, useModal, useVfm } from 'vue-final-modal';

import type { VFMPayload } from '@/helpers/vfm/types';

// Tracks slotId → modal close function for closeById()
const idGroup = new Map<string, () => Promise<string>>();
let vfmCssLoaded = false;

async function ensureVfmCss() {
	if (vfmCssLoaded) return;

	try {
		await import('@/styles/vfm.scss');
		vfmCssLoaded = true;
	} catch (error) {
		console.error('Failed to load VFM CSS:', error);
	}
}

// Lazy-loaded components (e.g. `() => import('@/components/...')`) need
// defineAsyncComponent in Vue 3. Plain component objects are passed through.
function resolveComponent(component: any): any {
	if (typeof component === 'function' && !component.__vccOpts && !component.__name) {
		return defineAsyncComponent(component);
	}
	return component;
}

const open = ({
	type = 'modal',
	props = { escToClose: true },
	onOpened = () => {},
	onBeforeClosed = () => {},
	onClosed = () => {},
	slotComponent,
	slotProps,
	slotId,
	onSlot,
}: VFMPayload) => {
	// resolve/reject are assigned synchronously inside the Promise constructor
	// before dismiss or the modal lifecycle callbacks can fire.
	let resolve!: (value: any) => void;
	let reject!: (reason?: any) => void;
	let isResolved = false;

	// dismiss is defined before useModal so it can be passed as a slot attr,
	// but it closes `modal` which is defined right after — safe because dismiss
	// is only ever called after the modal has been opened.
	let _modalClose: (() => Promise<string>) | undefined;

	const dismiss = (data?: any) => {
		_modalClose?.();
		resolve(data);
		isResolved = true;
	};

	const modal = useModal({
		component: markRaw(VueFinalModal),
		attrs: {
			type,
			overlayTransition: 'vfm-fade',
			contentTransition: 'vfm-fade',
			...props,
			onOpened: () => onOpened(),
			onBeforeClose: () => onBeforeClosed(),
			onClosed: () => {
				onClosed();
				if (slotId) idGroup.delete(slotId);
				if (!isResolved) resolve(null);
			},
		},
		slots: {
			default: {
				component: markRaw(resolveComponent(slotComponent)),
				attrs: {
					...slotProps,
					onDismiss: dismiss,
					...onSlot,
				},
			},
		},
	});

	_modalClose = modal.close;

	if (slotId) idGroup.set(slotId, modal.close);

	const promise = new Promise<any>((_resolve, _reject) => {
		resolve = _resolve;
		reject = _reject;
		modal.open();
	});

	return { promise, resolve, reject, isResolved, dismiss };
};

async function close() {
	try {
		await useVfm().closeAll();
	} catch (error) {
		console.error('VFM close error', error);
	}
}

async function closeById(id: string) {
	try {
		await idGroup.get(id)?.();
	} catch (error) {
		console.error('VFM close error', error);
	}
}

export const VFMHelper = { ensureVfmCss, open, close, closeById };
